/**
 * Content for the Video alert which is shown when the user clicks the "Watch
 * Video" button for a question in the skill delivery view. Also shown in
 * tethering.
 */

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import VideoPlayer from 'phoenix2/components/widgets/video-player';
import { shouldKeyTrigger } from 'phoenix2/utils/keyboard';
import {
    addKeyboardShortcuts,
    removeKeyboardShortcuts,
} from 'phoenix2/components/keyboard/keyboard-handler';
import { VIDEO_TYPE_TUTORIAL, VIDEO_TYPE_SKILL } from 'phoenix2/state/video/video';

// Dimensions that fit most Sparx videos at full size, without too much border
// If there's room on the screen, we allow this space for the video, otherwise
// we scale this box down to fit, horizontally and vertically
const DEFAULT_VIDEO_DIMENSIONS = {
    videoWidth: 1250,
    videoHeight: 652,
};

// Amount of space needed for alert title, margin etc. that can't be used for
// the video
const WIDTH_REDUCTION = 50;
const HEIGHT_REDUCTION = 207;

class VideoAlert extends React.Component {
    static displayName = 'VideoAlert';

    /**
     * PropTypes
     * videoId - (string) the id of the video being shown
     * forcePause - (boolean) if true, the video will be paused immediately
     * (used if the teacher locks a lesson)
     * skipToFinalFrame - (boolean) if true, the video will be set to the final
     * frame when it loads. This is used in tethering mode as a substitute for a
     * final frame image, which is not available
     * keyboardHandlerId - (string) id for the app's keyboard handler
     * onEndAlert - (func) callback for when the alert is dismissed
     * videoType - (string) type of video - e.g. "skill" or "tutorial"
     * disableSeek - (bool) if true, the user may not seek forward in the
     * video
     * disableSkipButton - (bool) if true, the user cannot press the button until the video is
     * complete or if it fails to load
     * enableCheatCode: If true, and if disableSeek=true and disableSkipButton=true then the user
     * can press the 'x' key to automatically skip to the end of the video. This
     * is enabled on test environments to get around mandatory videos
     * showSubtitles - (string) whether to enable subtitles (a vtt track) for
     * the video
     * corsSafe: controls whether we send credentials on video load
     * disableFinalFrameAtStart: if true, hides the video poster showing the
     * final frame for skill type videos
     */
    static propTypes = {
        // These come from own props:
        videoId: PropTypes.string,
        onEndAlert: PropTypes.func.isRequired,
        skillId: PropTypes.string,
        activityIndex: PropTypes.number,
        videoType: PropTypes.string.isRequired,
        disableSeek: PropTypes.bool,
        disableSkipButton: PropTypes.bool,
        enableCheatCode: PropTypes.bool,
        showSubtitles: PropTypes.bool,
        // Alternative text to show on the buttons. If not provided, the button will say
        // "Close video"
        buttonText: PropTypes.string,
        // Text to show beneath the video, giving instructions. Optional - if not provided, the
        // button is centred with no text
        instructionText: PropTypes.string,
        // Optional - Callback for when the user reaches the end of the video
        onReachedVideoEnd: PropTypes.func,
        // Whether to use a secondary button for the action
        useSecondaryButton: PropTypes.bool,
        // Optional - Replace the footer with a custom version that can include additional styling
        customFooter: PropTypes.element,
        // Optional - Extra height reduction based on the custom footer
        additionalHeightReduction: PropTypes.number,

        // These come from the container:
        forcePause: PropTypes.bool.isRequired,
        skipToFinalFrame: PropTypes.bool.isRequired,
        keyboardHandlerId: PropTypes.string,
        corsSafe: PropTypes.bool,
        disableFinalFrameAtStart: PropTypes.bool,
        autoPlay: PropTypes.bool,
        outroLength: PropTypes.number,
    };

    constructor(props) {
        super(props);

        // Work out how big the video should be given the screen size
        // (recalculates on resize)
        const videoDimensions = this.calculateVideoDimensions();

        this.state = {
            ...videoDimensions,
            reachedEnd: false,
            videoLoadFail: false,
        };
    }

    componentDidMount() {
        // Keyboard shortcuts:
        this._keyboardShortcutsId = addKeyboardShortcuts(
            {
                q: () => !this._shouldDisableCloseButton() && this.props.onEndAlert('close'),
                x: () => this.cheatCode(),
            },
            'video alert',
            this.props.keyboardHandlerId,
        );

        // Listen to screen resize:
        this.resizeListener = () => this.handleResize();
        window.addEventListener('resize', this.resizeListener);
    }

    componentWillUnmount() {
        // Remove keyboard shortcuts:
        removeKeyboardShortcuts(this._keyboardShortcutsId, this.props.keyboardHandlerId);
        // Remove listeners:
        window.removeEventListener('resize', this.resizeListener);
    }

    render() {
        const {
            videoId,
            forcePause,
            skipToFinalFrame,
            skillId,
            onEndAlert,
            activityIndex,
            videoType,
            disableSeek,
            showSubtitles,
            corsSafe,
            disableFinalFrameAtStart,
            buttonText,
            instructionText,
            useSecondaryButton,
            autoPlay,
            customFooter,
            outroLength,
        } = this.props;

        // If disableFinalFrameAtStart and the video type is Skill, hide the poster
        const hidePoster = disableFinalFrameAtStart && videoType === VIDEO_TYPE_SKILL;

        const { videoWidth, videoHeight, videoLoadFail } = this.state;

        const closeIcon =
            videoType === VIDEO_TYPE_TUTORIAL ? (
                <i className="button-icon-right fas fa-chevron-right" />
            ) : (
                <i className="button-icon-right fas fa-times" />
            );

        const skillVideo = (
            <VideoPlayer
                videoId={videoId}
                skillId={skillId}
                activityIndex={activityIndex}
                forcePause={forcePause}
                hidePoster={hidePoster}
                skipToFinalFrame={skipToFinalFrame}
                corsSafe={corsSafe}
                style={{ width: videoWidth, height: videoHeight }}
                videoType={videoType}
                onReachedEnd={() => this.reachedEnd()}
                disableSeek={disableSeek}
                videoLoadFail={() => this.videoLoadFail()}
                showSubtitles={showSubtitles}
                autoPlay={autoPlay}
                outroLength={outroLength}
            />
        );

        let buttonEnabled = true;

        // Disable the close button if forcing the user to watch, unless the
        // video failed to load or the user has watched to the end
        if (this._shouldDisableCloseButton()) {
            buttonEnabled = false;
        }

        const buttonClassnames = classNames('btn', 'alert-button', {
            'btn-disabled': !buttonEnabled,
            'btn-secondary': !!useSecondaryButton,
        });

        // The onboarding video saga listens for the alertEndStatus. Currently only the 'close'
        // reason is actioned (this completes the tutorial). The other two states, 'fail' and
        // 'close-without-reaching-end' do not trigger any behaviour.
        let alertEndStatus;
        if (videoLoadFail) {
            alertEndStatus = 'fail';
        } else if (
            videoType === VIDEO_TYPE_TUTORIAL &&
            !this.state.reachedEnd &&
            !this.props.enableCheatCode
        ) {
            alertEndStatus = 'close-without-reaching-end';
        } else {
            alertEndStatus = 'close';
        }

        const button = (
            <div
                tabIndex={buttonEnabled ? '0' : '-1'}
                key="close"
                className={buttonClassnames}
                onClick={() => buttonEnabled && onEndAlert(alertEndStatus)}
                onMouseDown={evt => {
                    // Prevent focussing when clicked:
                    evt.preventDefault();
                }}
                onKeyDown={evt =>
                    buttonEnabled && shouldKeyTrigger(evt) && onEndAlert(alertEndStatus)
                }
            >
                {buttonText || 'Close video'} {closeIcon}
            </div>
        );

        const footer = instructionText ? (
            <div className="alert-buttons video-alert-actions video-alert-actions-with-instructions">
                <div className="video-alert-instructions">{instructionText}</div>
                {button}
            </div>
        ) : (
            <div className="alert-buttons video-alert-actions">{button}</div>
        );

        return (
            <div className="video-alert">
                {skillVideo}
                {customFooter ? customFooter : footer}
            </div>
        );
    }

    _shouldDisableCloseButton() {
        const { disableSkipButton, enableCheatCode } = this.props;
        const { reachedEnd, videoLoadFail } = this.state;
        return disableSkipButton && !reachedEnd && !videoLoadFail && !enableCheatCode;
    }

    /**
     * Calculates how big an area to allow for in the alert for the video to fit
     * into. We can't use the actual video dimensions, as we need to show the
     * alert before the video has loaded. Also, there is no consistency in video
     * dimension. So we use the width/height of the most common set of video
     * dimensions (1250 x 652 - approx 33% of videos). Videos can't be shown
     * bigger than this, and if smaller will be centred within the box. We
     * shrink the box, maintaining aspect-ratio if the screen is not large
     * enough for it.
     * @returns {{videoWidth: number, videoHeight: number}}
     */
    calculateVideoDimensions() {
        // We can't measure the screen size at the moment (e.g. in unit tests)
        // so just use any size:
        if (!window.innerHeight || !window.innerWidth) {
            return DEFAULT_VIDEO_DIMENSIONS;
        }

        // If any additional height reduction has been passed in (e.g for custom footers)
        // then apply it here
        const { additionalHeightReduction } = this.props;
        const heightReduction = additionalHeightReduction
            ? additionalHeightReduction + HEIGHT_REDUCTION
            : HEIGHT_REDUCTION;

        const widthAvailable = window.innerWidth - WIDTH_REDUCTION;
        const heightAvailable = window.innerHeight - heightReduction;

        // No space to show video (extreme case)
        if (heightAvailable <= 0 || widthAvailable <= 0) {
            return {
                videoWidth: 0,
                videoHeight: 0,
            };
        }

        const scaleFactor = Math.min(
            widthAvailable / DEFAULT_VIDEO_DIMENSIONS.videoWidth,
            heightAvailable / DEFAULT_VIDEO_DIMENSIONS.videoHeight,
        );

        // No need to scale video, as there is enough room:
        if (scaleFactor >= 1.0) {
            return DEFAULT_VIDEO_DIMENSIONS;
        }

        // Scale the video to fit the screen, maintaining aspect ratio:
        return {
            videoWidth: Math.floor(scaleFactor * DEFAULT_VIDEO_DIMENSIONS.videoWidth),
            videoHeight: Math.floor(scaleFactor * DEFAULT_VIDEO_DIMENSIONS.videoHeight),
        };
    }

    /**
     * When the screen is resized, recalculate the video size
     */
    handleResize() {
        const { videoWidth, videoHeight } = this.state;

        const videoDimensions = this.calculateVideoDimensions();

        // Don't re-render unless it has changed
        if (
            videoDimensions.videoWidth !== videoWidth ||
            videoDimensions.videoHeight !== videoHeight
        ) {
            this.setState({
                ...videoDimensions,
            });
        }
    }

    /**
     * Record if the user gets to the end of the video (used for enabling the
     * button in tutorial videos), and fire the onReachedVideoEnd callback, if one was provided
     */
    reachedEnd() {
        if (!this.state.reachedEnd) {
            this.setState({ reachedEnd: true });
        }
        const { onReachedVideoEnd } = this.props;
        if (onReachedVideoEnd) {
            onReachedVideoEnd();
        }
    }

    /**
     * Record if the video failed to load
     */
    videoLoadFail() {
        if (!this.state.videoLoadFail) {
            this.setState({ videoLoadFail: true });
        }
    }

    /**
     * When the 'x' key is pressed, if enableCheatCode=true and
     * disableSkipButton=true and disableSeek=true, then enable the continue button even though
     * the user should be forced to watch the video.
     */
    cheatCode() {
        const { disableSkipButton, disableSeek, enableCheatCode, onEndAlert } = this.props;
        if (disableSkipButton && disableSeek && enableCheatCode) {
            onEndAlert('close');
        }
    }
}

export default VideoAlert;
